import React, { useState } from 'react';
import { Star, X } from 'lucide-react';
import { reviewsApi } from '../services/api';
import { ReviewCreate, ReviewUpdate } from '../types';
import { extractApiError, DEFAULT_ERROR_MESSAGE } from '../utils/errorUtils';

interface ReviewFormProps {
  fairId: number;
  existingReview?: {
    rating: number;
    title: string;
    comment: string;
  };
  onSuccess: () => void;
  onCancel: () => void;
}

export default function ReviewForm({ fairId, existingReview, onSuccess, onCancel }: ReviewFormProps) {
  const [rating, setRating] = useState(existingReview?.rating || 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState(existingReview?.title || '');
  const [comment, setComment] = useState(existingReview?.comment || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    if (title.trim().length < 3) {
      setError('Title must be at least 3 characters');
      return;
    }

    if (comment.trim().length < 10) {
      setError('Comment must be at least 10 characters');
      return;
    }

    setLoading(true);

    try {
      if (existingReview) {
        // Update existing review
        const updateData: ReviewUpdate = { rating, title: title.trim(), comment: comment.trim() };
        await reviewsApi.updateReview(fairId, updateData);
      } else {
        // Create new review
        const reviewData: ReviewCreate = {
          fair_id: fairId,
          rating,
          title: title.trim(),
          comment: comment.trim(),
        };
        await reviewsApi.createReview(reviewData);
      }

      onSuccess();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to submit review:', apiError);
      // Use fallback message if we get generic error
      const errorMessage = apiError.message === DEFAULT_ERROR_MESSAGE
        ? 'Failed to submit review. Please try again.'
        : apiError.message;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">
          {existingReview ? 'Edit Your Review' : 'Write a Review'}
        </h3>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Rating *
          </label>
          <div className="flex items-center space-x-1">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setRating(value)}
                onMouseEnter={() => setHoverRating(value)}
                onMouseLeave={() => setHoverRating(0)}
                className="p-1 transition-transform hover:scale-110"
                data-testid={`rating-${value}-review-button`}
                aria-label={`Rate ${value} star${value !== 1 ? 's' : ''}`}
              >
                <Star
                  className={`h-8 w-8 ${
                    value <= (hoverRating || rating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
            <span className="ml-3 text-sm text-gray-600">
              {rating > 0 ? `${rating} star${rating !== 1 ? 's' : ''}` : 'Select rating'}
            </span>
          </div>
        </div>

        {/* Title */}
        <div>
          <label htmlFor="review-title" className="block text-sm font-medium text-gray-700 mb-2">
            Review Title *
          </label>
          <input
            id="review-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 200))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Sum up your experience (e.g., 'Great produce!')"
            maxLength={200}
            required
            data-testid="review-title-input"
          />
          <p className="mt-1 text-sm text-gray-500">{title.length}/200 characters</p>
        </div>

        {/* Comment */}
        <div>
          <label htmlFor="review-comment" className="block text-sm font-medium text-gray-700 mb-2">
            Your Review *
          </label>
          <textarea
            id="review-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 2000))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Share your experience with this artist fair..."
            rows={6}
            maxLength={2000}
            required
            data-testid="review-comment-textarea"
          />
          <p className="mt-1 text-sm text-gray-500">{comment.length}/2000 characters</p>
        </div>

        {/* Actions */}
        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            data-testid="submit-review-button"
          >
            {loading ? 'Submitting...' : existingReview ? 'Update Review' : 'Submit Review'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            data-testid="cancel-review-button"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
